<template>
  <div>
    <slot name="loader">
      <div
        class="fixed top-0 left-0 flex items-center justify-center w-full h-screen bg-black bg-opacity-50 z-4000"
        v-if="showLoader"
      >
        <LoadingSpinner color="white"></LoadingSpinner>
      </div>
    </slot>

    <slot name="button" :startCheckout="startCheckout" />

    <slot name="progressBar">
      <div
        class="fixed top-0 left-0 flex items-center justify-center w-full h-screen bg-black bg-opacity-50 z-4000"
        v-if="isWaitingOnChargebee"
      >
        <div class="w-11/12 p-6 bg-white rounded md:w-1/2">
          <loading-bar label="Updating your account..."
                        description="You will be redirected once we've gotten everything set-up!"
                        v-model="progress"
                        class="my-4"
          ></loading-bar>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import api from "@/services/api";
import { LoadingSpinner } from '@sportsrecruits/playbook';
import { LoadingBar } from '@sportsrecruits/playbook';
import tracking from "@/components/Pricing/utils/tracking";

export default {
  components: {
    LoadingSpinner,
    LoadingBar,
  },
  props: {
    planId: {
      type: String,
      required: true,
    },
    checkoutData: {
      type: Object,
      required: true,
    },
    redirectTo: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chargebee: null,
      showLoader: false,
      isWaitingOnChargebee: false,
      progress: 0,
      isModalOpen: false,
    };
  },
  watch: {
    isModalOpen(value) {
      if (value) {
        document.getElementsByTagName('body')[0].classList.add('overflow-y-hidden');
      } else {
        document.getElementsByTagName('body')[0].classList.remove('overflow-y-hidden');
      }
    },
  },
  methods: {
    startCheckout() {
      tracking.purchase({
        event: "clicked_purchase_plan_cta",
        cta_location: this.planId,
      });

      this.openChargebeeModal();
    },
    ensureChargebeeCustomer() {
      this.showLoader = true;

      return api
        .post("api/v1/chargebee/customers", {
          userId: this.checkoutData.userId,
          emailId: this.checkoutData.emailId,
        })
        .finally(() => {
          this.showLoader = false;
        });
    },
    openChargebeeModal() {
      this.isModalOpen = true;

      return this.chargebee.openCheckout({
        hostedPage: () => {
          return this.ensureChargebeeCustomer().then(() => {
            return this.retrieveHostedPage();
          })
        },
        success: () => {
          this.waitForChargebee().then(() => this.redirectToNextPage());
        },
        error: (error) => {
          this.isModalOpen = false;

          if (error.responseJSON) {
            const errorCode = error.responseJSON.errorCode;

            swal({
              text: this.resolveErrorMessage(errorCode),
            });
          } else {
            console.error(error);
          }
        },
        close: () => {
          this.isModalOpen = false;
        },
      });
    },
    waitForChargebee() {
      return new Promise((resolve) => {
        this.isWaitingOnChargebee = true;

        this.checkIfFinished().then(() => {
          this.progress = 100;

          setTimeout(() => {
            resolve();
          }, 2000);
        });
      });
    },
    updateProgress() {
      if (this.isWaitingOnChargebee) {
        let progress = Math.min(this.progress + 1, 100);

        if (progress < 99) {
          setTimeout(() => {
            if (this.progress !== 100) {
              this.progress = progress;
            }
          }, this.getProgressTimeout(progress));
        }
      }
    },
    getProgressTimeout(progress) {
      switch(true) {
        case progress < 25:
          return 50;
        case progress < 35:
          return 100;
        case progress < 45:
          return 200;
        case progress < 75:
          return 500;
        case progress < 90:
          return 1000;
        case progress < 95:
          return 5000;
        default:
          return 1000;
      }
    },
    checkIfFinished() {
      return api
        .get("/api/v1/chargebee/checkout/finished")
        .then(({ isFinished }) => {
          if (!isFinished) {
            this.updateProgress();
            return this.checkIfFinished();
          }
        });
    },
    retrieveHostedPage() {
      return api.get("api/v1/chargebee/checkout/hostedPage", {
        userId: this.checkoutData.userId,
        planId: this.planId,
      }).catch(error => {
        swal({
          text: this.resolveErrorMessage(),
        });
      });
    },
    resolveErrorMessage(errorCode) {
      const defaultMessage =
        'An unexpected error occurred. Please contact support at <a href="mailto:at help@sportsrecruits.com">help@sportsrecruits.com</a>';

      return (
        {
          subscriptionExists: "A subscription already exists for this user.",
        }[errorCode] || defaultMessage
      );
    },
    redirectToNextPage() {
      window.location = this.redirectTo;
    },
  },
  beforeMount() {
    this.chargebee = Chargebee.init({
      site: this.site,
    });
  },
};
</script>
